/**
 * Settings -> Portal: what a client sees in the portal unless their own row
 * says otherwise.
 *
 * The TOP of three levels. This panel writes one `Setting` row; a client row
 * replaces it outright (so a single company can be given more as well as less),
 * and a contact row intersects whatever the company ends up with (so a person
 * can be given less than their company, never more). Nothing about that
 * precedence is computed here - it is resolved once on the server and travels on
 * the rows that need it.
 *
 * ── WHY SAVE IS A BUTTON HERE AND A TICK ON THE CONTACT ────────────────────
 *
 * `ContactPortalModules` saves on every tick, because it edits one person and
 * the current state is the whole message. This panel deliberately does not.
 * Editing a family of pills is several ticks, and at THIS level each
 * intermediate state would be a real access change for every client at once,
 * each with its own audit row - so an operator taking "invoices, estimates,
 * statement" down to "invoices" would leave three entries in the log, two of
 * which describe a portal nobody meant to ship. The shared `GrantPills` leaves
 * this choice to each caller for exactly that reason.
 *
 * ── THE MODE BUTTONS EXIST BECAUSE `null` IS NOT `[]` AND NOT "ALL" EITHER ──
 *
 * A row of ticked pills cannot say on its own whether this level decided
 * "everything" or decided nothing at all. Today the two grant the same access;
 * they stop being the same the moment somebody wants to add a page to the
 * product, because "no rule" picks it up and an explicit list does not. So the
 * state is named in words above the pills, and Custom seeds the whole
 * vocabulary rather than an empty array - see `seedCustomGrant`.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Clock3, Loader2, Save, ShieldAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { PORTAL_MODULES, type PortalModule } from "@wa/core/portal-modules";
import {
  PORTAL_PROJECT_TABS,
  PORTAL_PROJECT_TAB_FLOOR,
  type PortalProjectTab,
} from "@wa/core/portal-project-tabs";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  EMPTY_PORTAL_DEFAULTS,
  getPortalDefaults,
  grantMode,
  grantsEqual,
  putPortalDefaults,
  seedCustomGrant,
  type CrmPortalSettings,
  type GrantMode,
} from "../../../lib/portalSettings";
import { Btn, Card, CardHead } from "../../ui";
import { GrantPills } from "../clients/GrantPills";
import { FieldGroup, Note, SaveRow } from "./panel/fields";

/** The card body under a `CardHead`: `.crm-card` carries no padding of its own. */
const BODY = "px-6 pb-5 pt-3";

const MODES: readonly GrantMode[] = ["inherit", "custom"];

/**
 * Inherit or custom, said in words.
 *
 * A pair of pressed-state buttons rather than a switch, because neither state is
 * the "on" one: "no rule" is not the absence of a setting, it is a setting whose
 * meaning ("whatever the product ships with, now and later") a switch's off
 * position could not carry.
 */
function ModeChoice({
  mode,
  onMode,
  label,
  disabled,
}: {
  mode: GrantMode;
  onMode: (next: GrantMode) => void;
  /** Names the pair for a screen reader - the editor's own heading. */
  label: string;
  disabled: boolean;
}) {
  const t = useT();
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {MODES.map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={mode === m}
          disabled={disabled}
          onClick={() => onMode(m)}
          className={cn(
            "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors disabled:opacity-60",
            mode === m
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-muted-foreground hover:bg-accent",
          )}
        >
          {t(`settings.portal.mode.${m}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * One vocabulary, with its mode above it and the pills below.
 *
 * Generic over the two vocabularies rather than written twice: modules and
 * project tabs are different lists that must never mix as strings, but they are
 * the same THREE-STATE decision, and two copies of this block would be two
 * places for the seeding rule to drift.
 *
 * The pills stay on screen in both modes. Hiding them under Inherit would leave
 * the operator reading the word "inherit" with nothing saying what is inherited,
 * and clicking one straight out of Inherit is a legitimate edit - `GrantPills`
 * starts that click from the whole vocabulary here, so it means "all of them
 * except this", never "only this".
 */
function GrantEditor<T extends string>({
  title,
  desc,
  items,
  grant,
  onChange,
  labelFor,
  pinned,
  canManage,
  footer,
}: {
  title: string;
  desc: string;
  items: readonly T[];
  grant: T[] | null;
  onChange: (next: T[] | null) => void;
  labelFor: (item: T) => string;
  /** Names that cannot be switched off. Drawn ticked and dead by `GrantPills`. */
  pinned?: readonly T[];
  canManage: boolean;
  /** Whatever this particular vocabulary has to say for itself under the pills. */
  footer?: ReactNode;
}) {
  const t = useT();
  const mode = grantMode(grant);

  return (
    <FieldGroup title={title}>
      <p className="crm-sub !mt-0 text-[12.25px] leading-snug">{desc}</p>

      <ModeChoice
        mode={mode}
        label={title}
        disabled={!canManage}
        onMode={(next) => {
          // A second press on the mode the editor is already in must do nothing.
          // Re-seeding on it would throw away a selection the operator had just
          // finished making, out of a button that looks like it describes the
          // current state rather than replaces it.
          if (next === mode) return;
          onChange(next === "inherit" ? null : seedCustomGrant(items));
        }}
      />

      <GrantPills
        items={items}
        grant={grant}
        onChange={onChange}
        labelFor={labelFor}
        pinned={pinned}
        // `GrantPills` always draws its own way back to inherited and the panel
        // cannot switch that off, so it is given the SAME word as the mode
        // button above rather than a second name for one action - two labels for
        // the same act is how an operator learns to distrust a screen.
        clearLabel={t("settings.portal.mode.inherit")}
        disabled={!canManage}
      />

      <p className="crm-sub !mt-0 text-[11.5px] leading-snug">
        {mode === "inherit" ? t("settings.portal.inheritHint") : t("settings.portal.customHint")}
      </p>

      {footer}
    </FieldGroup>
  );
}

export function PortalSettings({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:crm.clients");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // What the operator is editing, and what the server last confirmed. Two copies
  // rather than one plus a dirty flag: the flag would have to be cleared by hand
  // in three places, and the one that got forgotten would leave a Save button
  // that refuses to light up.
  const [draft, setDraft] = useState<CrmPortalSettings>({ ...EMPTY_PORTAL_DEFAULTS });
  const [stored, setStored] = useState<CrmPortalSettings>({ ...EMPTY_PORTAL_DEFAULTS });
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    let alive = true;
    getPortalDefaults()
      .then((res) => {
        if (!alive) return;
        setDraft(res);
        setStored(res);
      })
      .catch(() => {
        // A failed read must not look like an installation with no rule saved.
        // Falling through to the empty defaults would draw every pill ticked and
        // invite an operator to "confirm" a portal they have not actually seen.
        if (alive) setFailed(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const dirty =
    !grantsEqual(draft.modules, stored.modules) || !grantsEqual(draft.projectTabs, stored.projectTabs);

  async function save() {
    setState("saving");
    try {
      // Both fields named, even the ones the operator did not touch. The route
      // merges on omission, so sending only the changed one would work - but
      // this panel presents the two as one decision behind one button, and a
      // body that matches what the screen showed is the one a later reader of
      // the audit row can check against.
      const res = await putPortalDefaults({ modules: draft.modules, projectTabs: draft.projectTabs });
      setDraft(res);
      setStored(res);
      setState("saved");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </Card>
    );
  }

  if (failed) {
    return (
      <Card>
        <CardHead title={t("settings.portal.title")} sub={t("settings.portal.desc")} />
        <div className={BODY}>
          <Note tone="bad" icon={<ShieldAlert className="size-4 shrink-0" />}>
            {t("settings.portal.loadFailed")}
          </Note>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHead title={t("settings.portal.title")} sub={t("settings.portal.desc")} />

      <div className={cn(BODY, "space-y-7")}>
        <GrantEditor<PortalModule>
          title={t("settings.portal.modules.title")}
          desc={t("settings.portal.modules.desc")}
          items={PORTAL_MODULES}
          grant={draft.modules}
          onChange={(modules) => setDraft((prev) => ({ ...prev, modules }))}
          labelFor={(m) => t(`clients.portalModules.m.${m}`)}
          canManage={canManage}
          footer={
            // The one state on this screen that can take a working portal away
            // from every client at once, and it is reachable by unticking six
            // pills without anything else on the page changing. It says so.
            draft.modules !== null && draft.modules.length === 0 ? (
              <Note tone="bad" icon={<ShieldAlert className="size-4 shrink-0" />}>
                {t("settings.portal.modules.emptyWarn")}
              </Note>
            ) : null
          }
        />

        <GrantEditor<PortalProjectTab>
          title={t("settings.portal.tabs.title")}
          desc={t("settings.portal.tabs.desc")}
          items={PORTAL_PROJECT_TABS}
          grant={draft.projectTabs}
          onChange={(projectTabs) => setDraft((prev) => ({ ...prev, projectTabs }))}
          labelFor={(tab) => t(`clients.portalTabs.t.${tab}`)}
          pinned={PORTAL_PROJECT_TAB_FLOOR}
          canManage={canManage}
          footer={
            // Said in the open rather than as a tooltip on the dead pill. The
            // shared `GrantPills` takes no per-pill title, and a hover hint is
            // the wrong place for this anyway: it answers "why will this one not
            // move", which is a question asked after clicking, on a control that
            // is disabled and therefore fires no hover on a touch screen.
            <p className="crm-sub !mt-0 text-[11.5px] leading-snug">{t("settings.portal.tabs.floorHint")}</p>
          }
        />

        {/* Not a detail. An operator who closes a page, reloads a client's portal
            and still sees it will conclude the save did not take, and the second
            save is the one that gets typed wrong. */}
        <Note tone="info" icon={<Clock3 className="size-4 shrink-0" />}>
          {t("settings.portal.cacheNote")}
        </Note>

        <SaveRow>
          {canManage ? (
            <Btn tone="dark" onClick={() => void save()} disabled={state === "saving" || !dirty}>
              {state === "saving" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {t("common.save")}
            </Btn>
          ) : (
            <span className="crm-sub !mt-0 text-[12px]">{t("settings.portal.readOnly")}</span>
          )}
          {state === "saved" ? (
            <span className="text-[12px] text-[var(--color-success)]">{t("common.saved")}</span>
          ) : null}
          {state === "error" ? (
            <span className="text-[12px] text-[var(--color-destructive)]">{t("common.error")}</span>
          ) : null}
        </SaveRow>
      </div>
    </Card>
  );
}
